/**
 * Admin Socket Hook
 * Manages real-time admin socket connections for verification management
 */

import { useCallback, useEffect, useState } from 'react';
import { socketService } from '../services/socketService';

interface AdminUser {
    id: string;
    name: string;
    role: string;
}

interface AdminSocketState {
    isConnected: boolean;
    isConnecting: boolean;
    error: string | null;
    admin: AdminUser | null;
}

interface QueueUpdate {
    count: number;
    pendingVerifications: any[];
}

export const useAdminSocket = (adminId?: string) => {
    const [state, setState] = useState<AdminSocketState>({
        isConnected: false,
        isConnecting: false,
        error: null,
        admin: null
    });

    const [queueUpdatesHandlers, setQueueUpdatesHandlers] = useState<((data: QueueUpdate) => void)[]>([]);

    console.log('Initializing admin socket connection for admin:', adminId);

    useEffect(() => {
        if (!adminId) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        setState(prev => ({ ...prev, isConnecting: true, error: null }));

        // Connect socket
        socketService.connect(token, adminId);

        // Set up connection listeners
        const handleConnect = () => {
            console.log('Admin socket connected 2');
            setState(prev => ({
                ...prev,
                isConnected: true,
                isConnecting: false,
                error: null
            }));
        };

        const handleDisconnect = (reason: string) => {
            console.log('Admin socket disconnected:', reason, '2');
            setState(prev => ({
                ...prev,
                isConnected: false,
                isConnecting: false,
                error: `Disconnected: ${reason}`
            }));
        };

        const handleError = (error: any) => {
            console.error('Admin socket error:', error);
            setState(prev => ({
                ...prev,
                error: error.message || 'Socket connection error',
                isConnecting: false
            }));
        };

        const handleQueueUpdate = (data: { count: number }) => {
            const queueUpdate: QueueUpdate = {
                count: data.count,
                pendingVerifications: []
            };
            queueUpdatesHandlers.forEach(handler => {
                try {
                    handler(queueUpdate);
                } catch (error) {
                    console.error('Error in queue update handler:', error);
                }
            });
        };

        // Set up listeners
        socketService.onConnect(handleConnect);
        socketService.onDisconnect(handleDisconnect);
        socketService.onError(handleError);
        socketService.onVerificationQueueCount(handleQueueUpdate);

        return () => {
            socketService.offConnect();
            socketService.offDisconnect();
            socketService.offError();
            socketService.offVerificationQueueCount();
            socketService.disconnect();
        };
    }, [adminId, queueUpdatesHandlers]);

    const subscribeToQueueUpdates = useCallback((handler: (data: QueueUpdate) => void) => {
        if (!state.isConnected) {
            return () => { }; // Return empty cleanup function
        }

        // Add handler to the list
        setQueueUpdatesHandlers(prev => [...prev, handler]);

        // Return cleanup function
        return () => {
            setQueueUpdatesHandlers(prev => prev.filter(h => h !== handler));
        };
    }, [state.isConnected]);

    const emitAdminAction = useCallback((action: string, data: any) => {
        if (!state.isConnected) {
            console.warn('Cannot emit admin action: socket not connected');
            return;
        }

        socketService.emit(action, { adminId, ...data });
    }, [state.isConnected, adminId]);

    return {
        connectionState: state,
        subscribeToQueueUpdates,
        emitAdminAction
    };
};

export default useAdminSocket;